import { html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { getCookie, setCookie } from '@cloudsuite/mosaic-shared/scripts/cookie.js'

export type MaxResultOption = {
  num: number
  selected: boolean
}

@customElement('csc-search-results-number')
export class CSCSearchResultsNumber extends LitElement {

  @property({ type: Array, attribute: 'max-results-options' })
  maxResultsOptions: MaxResultOption[] = []

  @state()
  private selectedNum: number = 0

  private readonly cookieKey = 'cs_maxresult'

  override firstUpdated() {
    if (!this.maxResultsOptions.length) {
      return
    }

    const cookieValue = getCookie(this.cookieKey)
    const parsed = cookieValue ? parseInt(cookieValue, 10) : undefined

    const match = this.maxResultsOptions.find(opt => opt.num === parsed)
    if (match) {
      this.selectedNum = match.num
    } else {
      const defaultOption = this.maxResultsOptions.find(opt => opt.selected) || this.maxResultsOptions[0]
      if (defaultOption) {
        this.selectedNum = defaultOption.num
        setCookie(this.cookieKey, String(this.selectedNum))
      }
    }
  }

  private handleChange(value: number) {
    if (this.selectedNum !== value) {
      this.selectedNum = value
      setCookie(this.cookieKey, String(value))
      this.dispatchEvent(new CustomEvent('results-number-changed', {
        detail: { number: value },
        bubbles: true,
        composed: true,
      }))
    }
  }

  override render() {
    return this.maxResultsOptions.length ? html`
      <cse-form-field class="csc-search-results-number">
        <cse-group>
          <cse-label for="search_results_number_dropdown" variant="body-s" class="label">
            ${t('label', { ns: 'csc-search-results-number', default: 'Count:' })}
          </cse-label>

          <cse-dropdown
            id="search_results_number_dropdown"
            value="${String(this.selectedNum)}"
            alignment="right"
            class="results-number-dropdown"
          >
            ${this.maxResultsOptions.map(option => html`
              <cse-option
                value="${String(option.num)}"
                @click="${() => this.handleChange(option.num)}"
                ?selected=${this.selectedNum === option.num}
              >
                ${option.num}
              </cse-option>
            `)}
          </cse-dropdown>
        </cse-group>
      </cse-form-field>
    ` :
      nothing
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-search-results-number': CSCSearchResultsNumber
  }
}
